import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'assessmentSearch',
  pure: false
})
export class AssessmentSearchPipe implements PipeTransform {
  transform(items: any[], searchText: string): any[] {
    if (!items || items.length === 0) return [];

    const term = this.normalizeSearchText(searchText);

    // If no search term, return all items without sorting
    if (!term) {
      return items;
    }

    const exactMatches: any[] = [];
    const startsWithItems: any[] = [];
    const includesItems: any[] = [];
    const wordMatches: any[] = [];

    items.forEach(item => {
      const displayValue = this.getDisplayValue(item);
      if (!displayValue) return; // Skip items without display value

      const normalizedDisplayValue = this.normalizeSearchText(displayValue);
      const originalDisplayValue = displayValue.toString().trim();

      // Check different types of matches
      if (normalizedDisplayValue === term) {
        // Exact match (highest priority)
        exactMatches.push(item);
      } else if (normalizedDisplayValue.startsWith(term)) {
        // Starts with search term
        startsWithItems.push(item);
      } else if (this.hasWordMatch(originalDisplayValue, searchText)) {
        // Word boundary match (for mixed content)
        wordMatches.push(item);
      } else if (normalizedDisplayValue.includes(term)) {
        // Contains search term anywhere
        includesItems.push(item);
      }
      // Note: No else clause - unmatched items are excluded
    });
    return [
      ...exactMatches,
      ...startsWithItems,
      ...wordMatches,
      ...includesItems
    ];
  }

  private normalizeSearchText(text: string): string {
    if (!text) return '';

    // Enhanced normalization for mixed content
    return text.toString()
      .trim()
      .toLowerCase()
      // Remove extra spaces but preserve single spaces
      .replaceAll(/\s+/g, ' ');
  }

  private compareStrings(a: string, b: string): number {
    // Enhanced comparison for mixed alphanumeric content
    const normalizedA = this.normalizeSearchText(a);
    const normalizedB = this.normalizeSearchText(b);

    return normalizedA.localeCompare(normalizedB, undefined, {
      numeric: true,           // Handle numbers properly (1, 2, 10 instead of 1, 10, 2)
      sensitivity: 'base',     // Case insensitive
      ignorePunctuation: false, // Keep punctuation for exact matching
      caseFirst: 'lower'       // Prefer lowercase first
    });
  }

  private hasWordMatch(displayValue: string, searchText: string): boolean {
    if (!displayValue || !searchText) return false;

    const normalizedDisplay = this.normalizeSearchText(displayValue);
    const normalizedSearch = this.normalizeSearchText(searchText);

    // Split both strings into words/tokens (handling mixed content)
    const displayWords = this.tokenize(normalizedDisplay);
    const searchWords = this.tokenize(normalizedSearch);

    // Check if any search word matches any display word
    return searchWords.some(searchWord =>
      displayWords.some(displayWord =>
        displayWord.startsWith(searchWord) ||
        displayWord === searchWord
      )
    );
  }

  private tokenize(text: string): string[] {
    if (!text) return [];

    // Enhanced tokenization for mixed content (letters, numbers, special chars)
    // Split on spaces, special characters, but keep alphanumeric groups together
    return text
      .split(/[[\s\-_.,;!@#$%^&*()+=\]{}|\\':"/<>?`~]+/)
      .filter(token => token.length > 0)
      .map(token => token.toLowerCase());
  }

  private getDisplayValue(item: any): string | null {
    if (!item) return null;

    if (typeof item === 'string') return item;

    // Check common property names for display values (in order of priority)
    const properties = [
      'displayExp',
      'templateName',
      'formName',
      'name',
      'title',
      'label',
      'text',
      'description',
      'valueExp',
      'value'
    ];

    for (const prop of properties) {
      if (item[prop] !== undefined && item[prop] !== null && item[prop] !== '') {
        return item[prop].toString().trim();
      }
    }

    return null;
  }

  // Enhanced relevance scoring (optional - for future use)
  private getRelevanceScore(item: any, searchText: string): number {
    const displayValue = this.getDisplayValue(item);
    if (!displayValue || !searchText) return 0;

    const normalizedDisplay = this.normalizeSearchText(displayValue);
    const normalizedSearch = this.normalizeSearchText(searchText);

    // Exact match gets highest score
    if (normalizedDisplay === normalizedSearch) return 100;

    // Starts with gets high score
    if (normalizedDisplay.startsWith(normalizedSearch)) return 90;

    // Word boundary match gets good score
    if (this.hasWordMatch(displayValue, searchText)) return 70;

    // Contains match gets lower score
    if (normalizedDisplay.includes(normalizedSearch)) return 50;

    return 0;
  }
}

